import { TRPCClientError } from '@trpc/client'

// Turns any thrown error into something safe to show a user. The server's
// errorFormatter already guarantees the *message* is friendly (Zod → summary,
// internal → generic, everything else → hand-written), so the client's job is
// small: detect transport failures the server never saw, flag transient errors
// so the UI can offer a retry, and pass through the structured fieldErrors.
const NETWORK = "We couldn't reach the server. Check your connection and try again."
const GENERIC = 'Something went wrong. Please try again.'

// Codes worth offering a "Try again" for — the request might just work next time.
const TRANSIENT_CODES = new Set(['INTERNAL_SERVER_ERROR', 'TIMEOUT', 'TOO_MANY_REQUESTS'])

export type FriendlyError = {
  message: string
  transient: boolean
  fieldErrors?: Record<string, string[]>
}

type Shaped = { code?: string; message?: string; fieldErrors?: Record<string, string[]> } | null

// Pure mapping — `null` means the request never reached the server (offline,
// DNS, CORS, transport timeout). Exported for unit testing.
export const friendlyFromShape = (shaped: Shaped): FriendlyError => {
  if (!shaped) return { message: NETWORK, transient: true }
  const code = shaped.code ?? 'INTERNAL_SERVER_ERROR'
  return {
    message: shaped.message?.trim() || GENERIC,
    transient: TRANSIENT_CODES.has(code),
    fieldErrors: shaped.fieldErrors,
  }
}

export const toFriendlyError = (error: unknown): FriendlyError => {
  if (error instanceof TRPCClientError) {
    const data = error.data as
      | { code?: string; fieldErrors?: Record<string, string[]> }
      | null
      | undefined
    if (!data) return friendlyFromShape(null) // no structured data → transport failure
    return friendlyFromShape({
      code: data.code,
      message: error.message,
      fieldErrors: data.fieldErrors,
    })
  }
  if (error instanceof Error && /network|fetch|load failed|timeout/i.test(error.message)) {
    return friendlyFromShape(null)
  }
  return { message: GENERIC, transient: true }
}
